import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  getTransactions,
  addTransaction,
  deleteTransaction,
  updateTransaction,
  getOutlets,
  getCategories,
} from "../apinew";
import {
  PlusCircle,
  Save,
  XCircle,
  Edit2,
  Trash2,
  TrendingUp,
  TrendingDown,
  AlertCircle,
} from "lucide-react";

export default function TransaksiPage() {
  const rawUser = localStorage.getItem("user");
  const user = rawUser ? JSON.parse(rawUser) : {};
  const userId = user?.user_id || user?.id || "";

  const [trx, setTrx] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [form, setForm] = useState({
    id: null,
    userId: userId,
    type: "income",
    amount: "",
    description: "",
    outletId: "",
    categoryId: "",
    date: "",
  });

  const resetForm = () =>
    setForm({
      id: null,
      userId: userId,
      type: "income",
      amount: "",
      description: "",
      outletId: "",
      categoryId: "",
      date: "",
    });

  const load = async () => {
    setLoading(true);
    setError("");

    try {
      const [o, c] = await Promise.all([getOutlets(), getCategories()]);
      setOutlets(Array.isArray(o) ? o : []);
      setCategories(Array.isArray(c) ? c : []);
    } catch (err) {
      console.error("Gagal load outlet/kategori:", err);
      setError(
        err.response?.data?.message ||
          err.message ||
          "Gagal memuat outlet/kategori"
      );
      setOutlets([]);
      setCategories([]);
    }

    try {
      const t = await getTransactions();
      setTrx(Array.isArray(t) ? t : []);
    } catch (err) {
      console.error("Gagal load transaksi:", err);
      setError((prev) =>
        prev ? prev + " | Gagal memuat transaksi" : "Gagal memuat transaksi"
      );
      setTrx([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddOrUpdate = async () => {
    if (!form.outletId) return alert("Outlet wajib dipilih");
    if (!form.categoryId) return alert("Kategori wajib dipilih");
    if (!form.date) return alert("Tanggal wajib diisi");
    if (!form.amount) return alert("Jumlah wajib diisi");

    const data = new FormData();
    data.append("user_id", userId);
    data.append("type", form.type);
    data.append("amount", form.amount);
    data.append("description", form.description);
    data.append("outlet_id", form.outletId);
    data.append("category_id", form.categoryId);
    data.append("date", form.date);

    try {
      setSaving(true);
      setError("");

      if (form.id) {
        await updateTransaction(form.id, data);
      } else {
        await addTransaction(data);
      }

      resetForm();
      await load();
    } catch (err) {
      console.error("Gagal simpan transaksi:", err);
      alert(
        err.response?.data?.message ||
          err.message ||
          "Gagal menyimpan transaksi"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (t) => {
    setForm({
      id: t.transaction_id,
      userId: t.user_id,
      type: t.type,
      amount: t.amount,
      description: t.description || "",
      outletId: t.outlet_id || t.outlet?.outlet_id || "",
      categoryId: t.category_id || t.category?.category_id || "",
      date: t.date ? String(t.date).split("T")[0] : "",
    });
  };

  const handleDelete = async (id) => {
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm("Yakin hapus transaksi ini?")) return;

    try {
      await deleteTransaction(id);
      await load();
    } catch (err) {
      console.error("Gagal hapus transaksi:", err);
      alert(
        err.response?.data?.message ||
          err.message ||
          "Gagal menghapus transaksi"
      );
    }
  };

  return (
    <div style={{ padding: "24px", minHeight: "100vh", background: "#f8fafc" }}>
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        style={{ marginBottom: "24px" }}
      >
        <h2
          style={{
            fontSize: "24px",
            fontWeight: "700",
            color: "#0f172a",
            marginBottom: "4px",
          }}
        >
          Transaksi
        </h2>
        <p style={{ fontSize: "14px", color: "#64748b", margin: 0 }}>
          Kelola catatan pemasukan dan pengeluaran Anda
        </p>
      </motion.div>

      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          style={{
            background: "#fef2f2",
            border: "1px solid #fee2e2",
            borderRadius: "8px",
            padding: "12px 16px",
            marginBottom: "16px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            color: "#991b1b",
          }}
        >
          <AlertCircle size={18} />
          <span style={{ fontSize: "14px" }}>{error}</span>
        </motion.div>
      )}

      {/* Form Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        style={{
          background: "#ffffff",
          borderRadius: "12px",
          border: "1px solid #e2e8f0",
          padding: "24px",
          marginBottom: "24px",
          boxShadow:
            "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
        }}
      >
        <h3
          style={{
            fontSize: "16px",
            fontWeight: "600",
            color: "#0f172a",
            marginBottom: "20px",
          }}
        >
          {form.id ? "Edit Transaksi" : "Tambah Transaksi Baru"}
        </h3>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
            gap: "16px",
          }}
        >
          <div>
            <label
              style={{
                display: "block",
                fontSize: "13px",
                fontWeight: "500",
                color: "#374151",
                marginBottom: "6px",
              }}
            >
              Tipe
            </label>
            <select
              value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value })}
              style={{
                width: "100%",
                padding: "8px 12px",
                border: "1px solid #e2e8f0",
                borderRadius: "6px",
                fontSize: "14px",
                color: "#0f172a",
                background: "#ffffff",
                outline: "none",
              }}
            >
              <option value="income">Pemasukan</option>
              <option value="expense">Pengeluaran</option>
            </select>
          </div>

          <div>
            <label
              style={{
                display: "block",
                fontSize: "13px",
                fontWeight: "500",
                color: "#374151",
                marginBottom: "6px",
              }}
            >
              Jumlah
            </label>
            <input
              type="number"
              min="0"
              placeholder="0"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              style={{
                width: "100%",
                padding: "8px 12px",
                border: "1px solid #e2e8f0",
                borderRadius: "6px",
                fontSize: "14px",
                color: "#0f172a",
                outline: "none",
              }}
            />
          </div>

          <div>
            <label
              style={{
                display: "block",
                fontSize: "13px",
                fontWeight: "500",
                color: "#374151",
                marginBottom: "6px",
              }}
            >
              Tanggal
            </label>
            <input
              type="date"
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
              style={{
                width: "100%",
                padding: "8px 12px",
                border: "1px solid #e2e8f0",
                borderRadius: "6px",
                fontSize: "14px",
                color: "#0f172a",
                outline: "none",
                transition: "all 0.2s ease",
                cursor: "pointer",
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = "#3b82f6";
                e.currentTarget.style.boxShadow =
                  "0 0 0 3px rgba(59, 130, 246, 0.1)";
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = "#e2e8f0";
                e.currentTarget.style.boxShadow = "none";
              }}
            />
          </div>

          <div>
            <label
              style={{
                display: "block",
                fontSize: "13px",
                fontWeight: "500",
                color: "#374151",
                marginBottom: "6px",
              }}
            >
              Outlet
            </label>
            <select
              value={form.outletId}
              onChange={(e) => setForm({ ...form, outletId: e.target.value })}
              style={{
                width: "100%",
                padding: "8px 12px",
                border: "1px solid #e2e8f0",
                borderRadius: "6px",
                fontSize: "14px",
                color: "#0f172a",
                background: "#ffffff",
                outline: "none",
                transition: "all 0.2s ease",
                cursor: "pointer",
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = "#3b82f6";
                e.currentTarget.style.boxShadow =
                  "0 0 0 3px rgba(59, 130, 246, 0.1)";
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = "#e2e8f0";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              <option value="">Pilih Outlet</option>
              {outlets.map((o) => (
                <option key={o.outlet_id} value={o.outlet_id}>
                  {o.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label
              style={{
                display: "block",
                fontSize: "13px",
                fontWeight: "500",
                color: "#374151",
                marginBottom: "6px",
              }}
            >
              Kategori
            </label>
            <select
              value={form.categoryId}
              onChange={(e) => setForm({ ...form, categoryId: e.target.value })}
              style={{
                width: "100%",
                padding: "8px 12px",
                border: "1px solid #e2e8f0",
                borderRadius: "6px",
                fontSize: "14px",
                color: "#0f172a",
                background: "#ffffff",
                outline: "none",
                transition: "all 0.2s ease",
                cursor: "pointer",
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = "#3b82f6";
                e.currentTarget.style.boxShadow =
                  "0 0 0 3px rgba(59, 130, 246, 0.1)";
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = "#e2e8f0";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              <option value="">Pilih Kategori</option>
              {categories.map((c) => (
                <option key={c.category_id} value={c.category_id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          <div style={{ gridColumn: "1 / -1" }}>
            <label
              style={{
                display: "block",
                fontSize: "13px",
                fontWeight: "500",
                color: "#374151",
                marginBottom: "6px",
              }}
            >
              Deskripsi
            </label>
            <input
              placeholder="Detail transaksi..."
              value={form.description}
              onChange={(e) =>
                setForm({ ...form, description: e.target.value })
              }
              style={{
                width: "100%",
                padding: "8px 12px",
                border: "1px solid #e2e8f0",
                borderRadius: "6px",
                fontSize: "14px",
                color: "#0f172a",
                outline: "none",
              }}
            />
          </div>
        </div>

        <div
          style={{
            display: "flex",
            gap: "8px",
            marginTop: "20px",
            justifyContent: "flex-end",
          }}
        >
          {form.id && (
            <button
              onClick={resetForm}
              style={{
                padding: "8px 16px",
                border: "1px solid #e2e8f0",
                borderRadius: "6px",
                background: "#ffffff",
                color: "#64748b",
                fontSize: "14px",
                fontWeight: "500",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#f3f4f6";
                e.currentTarget.style.boxShadow =
                  "0 2px 8px rgba(0, 0, 0, 0.08)";
                e.currentTarget.style.color = "#374151";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "#ffffff";
                e.currentTarget.style.boxShadow = "none";
                e.currentTarget.style.color = "#64748b";
              }}
            >
              <XCircle size={16} />
              Batal
            </button>
          )}
          <button
            onClick={handleAddOrUpdate}
            disabled={saving}
            style={{
              padding: "8px 16px",
              border: "none",
              borderRadius: "6px",
              background: form.id ? "#f59e0b" : "#3b82f6",
              color: "#ffffff",
              fontSize: "14px",
              fontWeight: "500",
              cursor: saving ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              opacity: saving ? 0.7 : 1,
              transition: "all 0.2s ease",
            }}
            onMouseEnter={(e) => {
              if (!saving) {
                e.currentTarget.style.opacity = "0.9";
                e.currentTarget.style.boxShadow = form.id
                  ? "0 4px 12px rgba(245, 158, 11, 0.3)"
                  : "0 4px 12px rgba(59, 130, 246, 0.3)";
                e.currentTarget.style.transform = "translateY(-1px)";
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = "1";
              e.currentTarget.style.boxShadow = "none";
              e.currentTarget.style.transform = "translateY(0)";
            }}
          >
            {form.id ? <Save size={16} /> : <PlusCircle size={16} />}
            {saving
              ? "Menyimpan..."
              : form.id
              ? "Perbarui"
              : "Tambah Transaksi"}
          </button>
        </div>
      </motion.div>

      {/* Transactions Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        style={{
          background: "#ffffff",
          borderRadius: "12px",
          border: "1px solid #e2e8f0",
          overflow: "hidden",
          boxShadow:
            "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
        }}
      >
        <div
          style={{ padding: "20px 24px", borderBottom: "1px solid #e2e8f0" }}
        >
          <h3
            style={{
              fontSize: "16px",
              fontWeight: "600",
              color: "#0f172a",
              margin: 0,
            }}
          >
            Daftar Transaksi
          </h3>
        </div>

        <div style={{ overflowX: "auto" }}>
          {loading ? (
            <div
              style={{ padding: "48px", textAlign: "center", color: "#94a3b8" }}
            >
              Memuat...
            </div>
          ) : trx.length === 0 ? (
            <div
              style={{ padding: "48px", textAlign: "center", color: "#94a3b8" }}
            >
              Belum ada transaksi
            </div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr
                  style={{
                    background: "#f8fafc",
                    borderBottom: "1px solid #e2e8f0",
                  }}
                >
                  <th
                    style={{
                      padding: "12px 24px",
                      textAlign: "left",
                      fontSize: "12px",
                      fontWeight: "600",
                      color: "#64748b",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                    }}
                  >
                    Tipe
                  </th>
                  <th
                    style={{
                      padding: "12px 24px",
                      textAlign: "left",
                      fontSize: "12px",
                      fontWeight: "600",
                      color: "#64748b",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                    }}
                  >
                    Jumlah
                  </th>
                  <th
                    style={{
                      padding: "12px 24px",
                      textAlign: "left",
                      fontSize: "12px",
                      fontWeight: "600",
                      color: "#64748b",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                    }}
                  >
                    Deskripsi
                  </th>
                  <th
                    style={{
                      padding: "12px 24px",
                      textAlign: "left",
                      fontSize: "12px",
                      fontWeight: "600",
                      color: "#64748b",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                    }}
                  >
                    Outlet
                  </th>
                  <th
                    style={{
                      padding: "12px 24px",
                      textAlign: "left",
                      fontSize: "12px",
                      fontWeight: "600",
                      color: "#64748b",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                    }}
                  >
                    Kategori
                  </th>
                  <th
                    style={{
                      padding: "12px 24px",
                      textAlign: "left",
                      fontSize: "12px",
                      fontWeight: "600",
                      color: "#64748b",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                    }}
                  >
                    Tanggal
                  </th>
                  <th
                    style={{
                      padding: "12px 24px",
                      textAlign: "center",
                      fontSize: "12px",
                      fontWeight: "600",
                      color: "#64748b",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                    }}
                  >
                    Aksi
                  </th>
                </tr>
              </thead>
              <tbody>
                {trx.map((t) => (
                  <tr
                    key={t.transaction_id}
                    style={{
                      borderBottom: "1px solid #f1f5f9",
                      transition: "all 0.2s ease",
                      cursor: "pointer",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "#f8fafc";
                      e.currentTarget.style.boxShadow =
                        "0 1px 3px rgba(0, 0, 0, 0.05)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "transparent";
                      e.currentTarget.style.boxShadow = "none";
                    }}
                  >
                    <td style={{ padding: "16px 24px" }}>
                      <div
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "6px",
                          padding: "4px 10px",
                          borderRadius: "6px",
                          fontSize: "12px",
                          fontWeight: "500",
                          background:
                            t.type === "income" ? "#d1fae5" : "#fee2e2",
                          color: t.type === "income" ? "#065f46" : "#991b1b",
                        }}
                      >
                        {t.type === "income" ? (
                          <TrendingUp size={14} />
                        ) : (
                          <TrendingDown size={14} />
                        )}
                        {t.type === "income" ? "Income" : "Expense"}
                      </div>
                    </td>
                    <td
                      style={{
                        padding: "16px 24px",
                        fontSize: "14px",
                        fontWeight: "500",
                        color: "#0f172a",
                      }}
                    >
                      Rp {Number(t.amount || 0).toLocaleString("id-ID")}
                    </td>
                    <td
                      style={{
                        padding: "16px 24px",
                        fontSize: "14px",
                        color: "#64748b",
                      }}
                    >
                      {t.description || "-"}
                    </td>
                    <td
                      style={{
                        padding: "16px 24px",
                        fontSize: "14px",
                        color: "#64748b",
                      }}
                    >
                      {t.outlet?.name || "-"}
                    </td>
                    <td
                      style={{
                        padding: "16px 24px",
                        fontSize: "14px",
                        color: "#64748b",
                      }}
                    >
                      {t.category?.name || "-"}
                    </td>
                    <td
                      style={{
                        padding: "16px 24px",
                        fontSize: "14px",
                        color: "#64748b",
                      }}
                    >
                      {t.date
                        ? new Date(t.date).toLocaleDateString("id-ID")
                        : "-"}
                    </td>
                    <td style={{ padding: "16px 24px", textAlign: "center" }}>
                      <div
                        style={{
                          display: "flex",
                          gap: "6px",
                          justifyContent: "center",
                        }}
                      >
                        <button
                          onClick={() => handleEdit(t)}
                          style={{
                            padding: "6px 10px",
                            border: "1px solid #fde68a",
                            borderRadius: "6px",
                            background: "#fef3c7",
                            color: "#92400e",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                            fontSize: "13px",
                            transition: "all 0.2s ease",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = "#fcd34d";
                            e.currentTarget.style.borderColor = "#f59e0b";
                            e.currentTarget.style.boxShadow =
                              "0 2px 8px rgba(245, 158, 11, 0.25)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = "#fef3c7";
                            e.currentTarget.style.borderColor = "#fde68a";
                            e.currentTarget.style.boxShadow = "none";
                          }}
                        >
                          <Edit2 size={14} />
                        </button>
                        <button
                          onClick={() => handleDelete(t.transaction_id)}
                          style={{
                            padding: "6px 10px",
                            border: "1px solid #fecaca",
                            borderRadius: "6px",
                            background: "#fee2e2",
                            color: "#991b1b",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                            fontSize: "13px",
                            transition: "all 0.2s ease",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = "#fca5a5";
                            e.currentTarget.style.borderColor = "#ef4444";
                            e.currentTarget.style.color = "#7f1d1d";
                            e.currentTarget.style.boxShadow =
                              "0 2px 8px rgba(239, 68, 68, 0.25)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = "#fee2e2";
                            e.currentTarget.style.borderColor = "#fecaca";
                            e.currentTarget.style.color = "#991b1b";
                            e.currentTarget.style.boxShadow = "none";
                          }}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </motion.div>
    </div>
  );
}
